import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Property } from '../../services/api';
import { buildBadges } from '../../domain/property/badges';
import { Badge } from '../../design-system/components/Badge';
import { COMPARE_MAX } from '../../domain/compare/compareStore';
import { buildLoc, buildImg } from './mapHelpers';
import { formatUnits } from '../../domain/property/formatters';

type SheetSnap = 'collapsed' | 'mid' | 'expanded';

interface BottomSheetProps {
  sheetSnap: SheetSnap;
  setSheetSnap: React.Dispatch<React.SetStateAction<SheetSnap>>;
  sheetCards: Property[];
  selectedProperty: Property | null;
  handleMarkerClick: (property: Property) => void;
  handleToggleCompare: (e: React.MouseEvent, propertyId: number) => void;
  compareIds: number[];
  favoriteIds: Set<number>;
  handleToggleFavorite: (e: React.MouseEvent, propertyId: number) => void;
}

// BottomSheet — 모바일(< lg) 지도 하단 목록 시트. lg+ 는 좌측 패널이 카드 리스트 담당.
// 이력: 2026-05-19 피드백("모바일은 지도 중심으로 검색") 으로 hidden →
//       2026-09-22 대표 결정으로 v2 목업 03(전체 지도 + 하단 캐러셀) 대로 노출.
// 5/19 재발 방지: 시트가 지도를 가리지 않도록 높이 = 내용 높이(카드 1줄 캐러셀)만.
//   구 14/42/70% 스냅·선택 강조 카드 블록 제거. 접힘 = 핸들+헤더만.
// 선택 마커 → 해당 카드로 가로 스크롤 + 강조(포커스). 접혀 있으면 펼침.
export const BottomSheet: React.FC<BottomSheetProps> = ({
  sheetSnap,
  setSheetSnap,
  sheetCards,
  selectedProperty,
  handleMarkerClick,
  handleToggleCompare,
  compareIds,
  favoriteIds,
  handleToggleFavorite,
}) => {
  const open = sheetSnap !== 'collapsed';
  const cardRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const selectedId = selectedProperty?.id ?? null;

  // 마커 탭 → 시트 펼침 + 해당 카드로 스크롤
  useEffect(() => {
    if (selectedId === null) return;
    setSheetSnap((s) => (s === 'collapsed' ? 'mid' : s));
    const t = setTimeout(() => {
      const el = cardRefs.current.get(selectedId);
      el?.scrollIntoView?.({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    }, 60);
    return () => clearTimeout(t);
  }, [selectedId, setSheetSnap]);

  return (
    <div
      className="absolute inset-x-0 bottom-0 z-[10] rounded-t-ca-4xl bg-frame pb-3 lg:hidden"
      style={{ boxShadow: '0 -8px 24px rgba(15,23,42,.16)' }}
      data-testid="map-bottom-sheet"
      data-open={open ? 'true' : 'false'}
    >
      {/* 핸들 — 탭하면 접힘/펼침 */}
      <button
        type="button"
        onClick={() => setSheetSnap((s) => (s === 'collapsed' ? 'mid' : 'collapsed'))}
        className="block w-full pb-2 pt-2"
        aria-label={open ? '목록 접기' : '목록 펼치기'}
        aria-expanded={open}
      >
        <div className="mx-auto h-1 w-10 rounded-full bg-ca-line" />
      </button>

      {/* 헤더 (사용자 피드백 2026-05-19: 한 줄, 지역명 생략). SEO: 단지 목록 섹션 제목(h2). */}
      <div className={`flex items-center justify-between px-4 ${open ? 'pb-2.5' : 'pb-0'}`}>
        <h2 className="flex items-baseline gap-1.5 text-[13px] font-bold text-ink">
          <span className="sr-only">대구·경북 분양 단지 목록 — </span>
          <span className="text-muted">현재 지도 영역 :</span>
          <span className="text-accent tabular-nums">{sheetCards.length.toLocaleString()}</span>
          <span>단지</span>
        </h2>
        {/* 모바일 비교 진입점 — 플로팅 비교 바(lg+ 전용)가 시트를 가리지 않도록 헤더로 이동 */}
        {compareIds.length > 0 && (
          <Link
            to="/compare"
            className="flex items-center gap-1 rounded-full bg-ink px-3 py-1.5 text-[11.5px] font-extrabold tracking-tight text-white"
          >
            단지 비교 {compareIds.length}/{COMPARE_MAX}
            <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M5 12h14" /><path d="m12 5 7 7-7 7" />
            </svg>
          </Link>
        )}
      </div>

      {/* 카드 가로 캐러셀 (목업 `.mcard`: 300px · 썸네일 76px · 배지 1개) */}
      {open && (
        <div
          className="flex snap-x scroll-px-4 gap-2.5 overflow-x-auto px-4 pb-1"
          style={{ scrollbarWidth: 'none' }}
        >
          {sheetCards.length === 0 ? (
            <div className="w-full py-5 text-center text-[12.5px] text-muted">
              이 영역에 표시할 단지가 없어요. 지도를 이동하거나 필터를 해제해보세요.
            </div>
          ) : (
            sheetCards.map((property) => {
              const selected = selectedId === property.id;
              const img = buildImg(property);
              // 배지: 대표혜택 → 할인율 → 분양예정 순(buildBadges) 중 1개. 다른 상태 문구 없음.
              const badge = buildBadges(property as any)[0];
              return (
                // 버튼 중첩(HTML 위반) 방지 — 카드 본체 버튼과 찜/비교 버튼을 형제로 배치 (2026-07-07)
                <div
                  key={property.id}
                  ref={(el) => {
                    if (el) cardRefs.current.set(property.id, el);
                    else cardRefs.current.delete(property.id);
                  }}
                  className="relative w-[300px] max-w-[calc(100vw-56px)] shrink-0 snap-start"
                  data-selected={selected ? 'true' : undefined}
                >
                  <button
                    type="button"
                    onClick={() => handleMarkerClick(property)}
                    aria-current={selected ? 'true' : undefined}
                    className={`flex w-full gap-2.5 rounded-[14px] border bg-frame p-2.5 pr-10 text-left ${
                      selected ? 'border-accent ring-1 ring-accent' : 'border-ca-line'
                    }`}
                  >
                    <div className="h-[76px] w-[76px] shrink-0 overflow-hidden rounded-ca-md bg-block">
                      {img && (
                        <img src={img} alt={property.title} loading="lazy" className="h-full w-full object-cover" />
                      )}
                    </div>
                    <div className="flex min-w-0 flex-1 flex-col justify-center gap-1">
                      {badge && (
                        <span className="self-start">
                          <Badge tone={badge.tone} size="tag" ariaLabel={badge.ariaLabel}>{badge.label}</Badge>
                        </span>
                      )}
                      <div className="text-[14px] font-bold tracking-tight text-ink line-clamp-1">
                        {property.title}
                      </div>
                      <div className="text-[11.5px] text-muted line-clamp-1">
                        {buildLoc(property)}
                        {formatUnits(property, { short: true }) ? ` · ${formatUnits(property, { short: true })}` : ''}
                      </div>
                    </div>
                  </button>
                  {/* 찜(하트) — 카드에서 바로 관심단지 등록 (2026-07-07) */}
                  <button
                    type="button"
                    onClick={(e) => handleToggleFavorite(e, property.id)}
                    aria-label={favoriteIds.has(property.id) ? '관심단지 해제' : '관심단지 등록'}
                    aria-pressed={favoriteIds.has(property.id)}
                    className="absolute right-1.5 top-1.5 flex h-7 w-7 items-center justify-center rounded-full bg-frame/92 active:translate-y-[1px]"
                  >
                    <svg width={15} height={15} viewBox="0 0 24 24"
                      fill={favoriteIds.has(property.id) ? 'var(--ca-accent)' : 'none'}
                      stroke={favoriteIds.has(property.id) ? 'var(--ca-accent)' : 'var(--ca-muted)'}
                      strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                      <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z" />
                    </svg>
                  </button>
                  {/* 비교 담기 — 하트 아래 (2026-07-07 단지비교) */}
                  <button
                    type="button"
                    onClick={(e) => handleToggleCompare(e, property.id)}
                    aria-label={compareIds.includes(property.id) ? '비교함에서 빼기' : '비교함에 담기'}
                    aria-pressed={compareIds.includes(property.id)}
                    className={`absolute right-1.5 top-10 flex h-7 min-w-7 items-center justify-center rounded-full px-1 text-[9.5px] font-extrabold active:translate-y-[1px] ${
                      compareIds.includes(property.id) ? 'bg-accent text-white' : 'bg-block text-muted'
                    }`}
                  >
                    VS
                  </button>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};
